// apps/web/src/components/parent/ApprovalDashboard.tsx
// Parent approval dashboard. Shows pending checklist items with photos, AI feedback,
// and timeline. Supports approve, return-with-comment, and bulk approve.

import { useState, useEffect, useCallback } from 'react';
import { getDateKey } from '@homework-app/shared';
import type { DailyChecklist } from '@homework-app/shared';
import { ApprovalItem } from './ApprovalItem';

interface ApprovalApiService {
  getChecklist(dateKey: string, studentId?: string): Promise<DailyChecklist>;
  updateChecklistStatus(itemId: string, status: string, comment?: string): Promise<void>;
  approveAll(studentId: string, dateKey: string): Promise<void>;
  getNotificationCount(): Promise<{ count: number }>;
}

interface Props {
  studentId: string;
  studentName: string;
  apiService: ApprovalApiService;
}

const glass: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12
};

export function ApprovalDashboard({ studentId, studentName, apiService }: Props) {
  const [checklist, setChecklist] = useState<DailyChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateKey] = useState(() => getDateKey(new Date()));
  const [unreadCount, setUnreadCount] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, notifs] = await Promise.all([
        apiService.getChecklist(dateKey, studentId),
        apiService.getNotificationCount().catch(() => ({ count: 0 }))
      ]);
      setChecklist(data);
      setUnreadCount(notifs.count);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load checklist';
      setError(msg);
      console.error('Failed to load approval data:', err);
    } finally {
      setLoading(false);
    }
  }, [apiService, dateKey, studentId]);

  useEffect(() => {
    loadData();

    // Poll for updates every 30 seconds while dashboard is open
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleApprove = async (itemId: string) => {
    try {
      await apiService.updateChecklistStatus(itemId, 'approved');
      await loadData();
    } catch (err) {
      console.error('Failed to approve:', err);
      setError('Failed to approve item. Please try again.');
    }
  };

  const handleReturn = async (itemId: string, comment: string) => {
    try {
      await apiService.updateChecklistStatus(itemId, 'returned', comment);
      await loadData();
    } catch (err) {
      console.error('Failed to return:', err);
      setError('Failed to return item. Please try again.');
    }
  };

  const handleApproveAll = async () => {
    try {
      await apiService.approveAll(studentId, dateKey);
      await loadData();
    } catch (err) {
      console.error('Failed to approve all:', err);
      setError('Failed to approve all. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#8b8b9e' }}>
        Loading checklist...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.1)', borderRadius: 8, color: '#fca5a5', marginBottom: '1rem' }}>
        {error}
        <button
          onClick={loadData}
          style={{ marginLeft: '1rem', color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!checklist || checklist.items.length === 0) {
    return (
      <div style={{ ...glass, padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ color: '#8b8b9e', marginBottom: '0.5rem' }}>No checklist items for today.</div>
        <div style={{ fontSize: '0.8rem', color: '#5a5a6e' }}>
          Items are generated automatically when {studentName} opens the app.
        </div>
      </div>
    );
  }

  const pendingItems = checklist.items.filter(i => i.status === 'pending_parent');
  const approvedItems = checklist.items.filter(i => i.status === 'approved');
  const otherItems = checklist.items.filter(i => i.status !== 'pending_parent' && i.status !== 'approved');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, color: '#f0f0f5', fontSize: '1rem' }}>
            {studentName}'s Daily Checklist
          </h3>
          <div style={{ fontSize: '0.8rem', color: '#8b8b9e', marginTop: '0.25rem' }}>
            {dateKey}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: checklist.all_approved ? '#10b981' : '#f0f0f5' }}>
            {checklist.completed_count}/{checklist.total_count}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#8b8b9e' }}>approved</div>
          {unreadCount > 0 && (
            <div style={{
              marginTop: '0.25rem',
              fontSize: '0.7rem',
              background: 'rgba(139,92,246,0.2)',
              color: '#a78bfa',
              padding: '2px 8px',
              borderRadius: 10,
              border: '1px solid rgba(139,92,246,0.3)'
            }}>
              {unreadCount} new
            </div>
          )}
        </div>
      </div>

      {/* All approved banner */}
      {checklist.all_approved && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(16,185,129,0.12)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 8,
          color: '#6ee7b7',
          textAlign: 'center',
          fontWeight: 500
        }}>
          All done! {checklist.reward ? `Screen time earned: ${checklist.reward.reward_text}` : 'Checklist complete!'}
        </div>
      )}

      {/* Bulk approve */}
      {pendingItems.length > 1 && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={handleApproveAll}
            style={{
              width: '100%',
              padding: '0.6rem',
              background: 'rgba(16,185,129,0.15)',
              color: '#10b981',
              border: '1px solid rgba(16,185,129,0.35)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.9rem',
              fontFamily: 'inherit'
            }}
          >
            Approve All Pending ({pendingItems.length} items)
          </button>
        </div>
      )}

      {/* Pending section */}
      {pendingItems.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#a78bfa', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Needs Your Approval ({pendingItems.length})
          </h4>
          {pendingItems.map(item => (
            <ApprovalItem
              key={item.id}
              item={item}
              onApprove={handleApprove}
              onReturn={handleReturn}
            />
          ))}
        </div>
      )}

      {/* In-progress / other items */}
      {otherItems.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#8b8b9e', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            In Progress
          </h4>
          {otherItems.map(item => (
            <ApprovalItem
              key={item.id}
              item={item}
              onApprove={handleApprove}
              onReturn={handleReturn}
              compact
            />
          ))}
        </div>
      )}

      {/* Approved section */}
      {approvedItems.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#10b981', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Approved ({approvedItems.length})
          </h4>
          {approvedItems.map(item => (
            <ApprovalItem
              key={item.id}
              item={item}
              onApprove={handleApprove}
              onReturn={handleReturn}
              compact
            />
          ))}
        </div>
      )}

      {/* Reward status */}
      {checklist.reward && !checklist.all_approved && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem 1rem',
          ...glass,
          color: '#8b8b9e',
          fontSize: '0.85rem',
          textAlign: 'center'
        }}>
          Reward waiting: {checklist.reward.reward_text}
        </div>
      )}
    </div>
  );
}
